import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { InitiativeTypeField } from '@src/features/ActionPaths/Wizard/components/fields/InitiativeType';
import { NotesField } from '@src/features/ActionPaths/Wizard/components/fields/Notes';
import { RemediationMethodField } from '@src/features/ActionPaths/Wizard/components/fields/RemediationMethod';
import { ScopeCodeField } from '@src/features/ActionPaths/Wizard/components/fields/ScopeCode';
import { useWizardContext } from '@src/features/ActionPaths/Wizard/context';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useFormFieldArray } from '@uhg-abyss/web/hooks/useFormFieldArray';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';

import { Table } from './components/Table';
import { Styles } from './includes/styles';

/**
 * RemediateRecords
 *
 * Provides the user with a screen to browse the risk records found based on the entrance criteria (filters) specified.
 *
 * @param assets
 * @param defaultValues
 * @param form
 * @param isLoading
 * @returns {Element}
 * @constructor
 */
export function RemediateRecords({ assets = {}, defaultValues = {}, form = {}, isLoading = false }) {
  const { actionPath, currentStep, mode } = useWizardContext();

  const sources = assets?.ListSources?.data || [];
  const actions = assets?.ListActions?.data || [];
  const chronoUnits = assets?.ListChronoUnits?.data || [];

  const { currentRoute } = useRoutesContext();

  const { append, fields, remove, replace, swap } = useFormFieldArray({
    control: form?.control,
    name: 'remediation.assignments',
  });

  const actionPathScopeCode = form.getValues('remediation.actionPathScopeCode');
  const remediationMethod = form.getValues('remediation.remediationMethod');
  const initiativeType = form.getValues('remediation.initiativeType');
  const notes = form.getValues('remediation.notes');

  let isActiveAndManual = false;

  if (
    typeof actionPath?.status === 'string' &&
    actionPath.status.includes('ACTIVE') &&
    actionPath?.manualAssociation === true
  ) {
    isActiveAndManual = true;
  }

  /**
   * Sets an initial row.
   */
  useEffect(() => {
    (async () => {
      if (isEmpty(fields)) {
        replace([
          {
            actionId: '',
            actionName: '',
            impactedSource: '',
            retryAttempts: '',
            retryInterval: '',
            retryIntervalUnit: '',
          },
        ]);
      }
    })();
  }, [fields]);

  /**
   * validate after setting initial row.
   */
  useEffect(() => {
    if (fields?.length === 1) {
      form.validate(
        `remediation.assignments.0.impactedSource`,
        () => {},
        () => {}
      );
    }
  }, [fields]);

  /**
   * handleReset
   *
   * @TODO - Needs description.
   *
   * @returns {Promise<void>}
   */
  const handleReset = async () => {
    await form.setValue('remediation.assignments', defaultValues?.remediation?.assignments);
    replace(defaultValues?.remediation?.assignments);
  };

  /**
   * validateField
   *
   * @TODO - Needs description.
   *
   * @param field
   */
  const validateField = (field) => {
    form.validate(
      `remediation[${field}]`,
      () => {},
      () => {}
    );
  };

  /**
   * Validate all repeatable fields within a row when a value changes.
   */
  useEffect(() => {
    if (isLoading === false) {
      if (!isEmpty(actionPathScopeCode)) {
        validateField('actionPathScopeCode');
      }
      if (!isEmpty(remediationMethod)) {
        validateField('remediationMethod');
      }
      if (!isEmpty(initiativeType)) {
        validateField('initiativeType');
      }
      validateField('notes');
    }
  }, [actionPathScopeCode, remediationMethod, notes, isLoading, initiativeType]);

  return (
    <ErrorHandler location="src/features/ActionPaths/Wizard/components/steps/RemediateRecords/RemediateRecords.jsx">
      <Styles>
        <Grid>
          <Grid.Col
            css={{ paddingTop: themeConfiguration?.theme?.space?.lg }}
            span={{
              xs: '100%',
            }}
          >
            <Heading offset={1}>{currentStep?.label} </Heading>
            <p>{currentStep?.description}</p>
            <Box
              color="$gray1"
              css={{
                border: '1px solid',
                borderColor: themeConfiguration?.theme?.colors?.gray3,
                position: 'relative',
              }}
              height="auto"
            >
              <Grid>
                <Grid.Col span={{ lg: 3, md: 6, xs: 12 }}>
                  <RemediationMethodField form={form} isActiveAndManual={isActiveAndManual} />
                  <InitiativeTypeField assets={assets} form={form} />
                </Grid.Col>
                <Grid.Col span={{ lg: 6, md: 6, xs: 12 }}>
                  {mode !== 'manual' && <ScopeCodeField form={form} />}
                  <NotesField form={form} />
                </Grid.Col>
              </Grid>
            </Box>
          </Grid.Col>

          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Layout.Group alignLayout="right">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                id="addField"
                isDisabled={isActiveAndManual}
                onClick={() => {
                  return append({
                    actionId: '',
                    actionName: '',
                    impactedSource: '',
                    retryAttempts: '',
                    retryInterval: '',
                    retryIntervalUnit: '',
                  });
                }}
                variant="custom"
              >
                <Flex alignItems="center">
                  <div>
                    <IconSymbol icon="add_circle" variant="filled" />
                    <IconSymbol icon="add_circle" variant="outlined" />
                  </div>
                  <div>Add Action</div>
                </Flex>
              </Link>
            </Layout.Group>
            <Table
              actions={actions}
              append={append}
              chronoUnits={chronoUnits}
              dataKey={`${currentRoute?.key}-remediateRecords`}
              fields={fields}
              form={form}
              handleReset={handleReset}
              isActiveAndManual={isActiveAndManual}
              isLoading={isLoading}
              remove={remove}
              replace={replace}
              sources={sources}
              swap={swap}
            />
          </Grid.Col>
        </Grid>
      </Styles>
    </ErrorHandler>
  );
}
